import { type RefObject, useEffect, useState } from 'react';

/** Highlights the native vertical scrollbar as one control, including while dragging. */
export const useScrollbarInteraction = (containerRef: RefObject<HTMLDivElement | null>) => {
  const [isScrollbarActive, setIsScrollbarActive] = useState(false);

  useEffect(() => {
    const container = containerRef.current;

    if (!container) {
      return;
    }

    let isDragging = false;

    /** Uses the actual scrollbar gutter so content, padding, and borders do not trigger hover. */
    const isOverScrollbar = (event: PointerEvent) => {
      if (container.scrollHeight <= container.clientHeight || !container.offsetWidth) {
        return false;
      }

      const rect = container.getBoundingClientRect();
      const styles = window.getComputedStyle(container);
      const borderLeft = parseFloat(styles.borderLeftWidth);
      const borderRight = parseFloat(styles.borderRightWidth);
      const scrollbarWidth =
        container.offsetWidth - container.clientWidth - borderLeft - borderRight;
      const x = ((event.clientX - rect.left) * container.offsetWidth) / rect.width;
      const y = ((event.clientY - rect.top) * container.offsetHeight) / rect.height;
      // clientLeft includes the scrollbar width when the browser places it on the left in RTL.
      const scrollbarLeft =
        container.clientLeft > borderLeft
          ? borderLeft
          : container.clientLeft + container.clientWidth;

      return (
        scrollbarWidth > 0 &&
        x >= scrollbarLeft &&
        x < scrollbarLeft + scrollbarWidth &&
        y >= container.clientTop &&
        y < container.clientTop + container.clientHeight
      );
    };

    /** Keeps the highlight stable while the native scrollbar owns the drag. */
    const handlePointerMove = (event: PointerEvent) => {
      if (event.pointerType !== 'touch') {
        setIsScrollbarActive(isDragging || isOverScrollbar(event));
      }
    };

    /** Starts a drag only from the scrollbar, never from the scrollable content. */
    const handlePointerDown = (event: PointerEvent) => {
      if (event.button === 0 && event.pointerType !== 'touch' && isOverScrollbar(event)) {
        isDragging = true;
        setIsScrollbarActive(true);
      }
    };

    /** Restores hover according to the release position, including releases outside the box. */
    const handlePointerUp = (event: PointerEvent) => {
      if (isDragging) {
        isDragging = false;
        setIsScrollbarActive(isOverScrollbar(event));
      }
    };

    /** Clears hover on exit without interrupting an active native drag. */
    const handlePointerLeave = () => {
      if (!isDragging) {
        setIsScrollbarActive(false);
      }
    };

    /** Clears interaction when the browser cancels it or loses focus. */
    const resetInteraction = () => {
      isDragging = false;
      setIsScrollbarActive(false);
    };

    container.addEventListener('pointermove', handlePointerMove);
    container.addEventListener('pointerdown', handlePointerDown);
    container.addEventListener('pointerleave', handlePointerLeave);
    document.addEventListener('pointerup', handlePointerUp, true);
    document.addEventListener('pointercancel', resetInteraction, true);
    window.addEventListener('blur', resetInteraction);

    return () => {
      container.removeEventListener('pointermove', handlePointerMove);
      container.removeEventListener('pointerdown', handlePointerDown);
      container.removeEventListener('pointerleave', handlePointerLeave);
      document.removeEventListener('pointerup', handlePointerUp, true);
      document.removeEventListener('pointercancel', resetInteraction, true);
      window.removeEventListener('blur', resetInteraction);
    };
  }, [containerRef]);

  return isScrollbarActive;
};
